import { Box, HStack, Span, Stack, Text } from '@chakra-ui/react';
import type { Dispatch, FC, SetStateAction } from 'react';
import { useAppStore, type User } from '@halo/chat-sdk-react';
import { UserItem, Empty } from '@halo/ui-chat-react';
import { useNavigate } from 'react-router';
import type { ReactNode } from 'react';
import { size, gt } from 'lodash';
import { useShallowEnhance } from '@halo/zustand-enhance';
import { MyTrans } from '@halo/ui-react';
import { useTranslation } from 'react-i18next';
import Paths from '@/configs/router/paths';
import { useActionsStore } from '@/store/actions';
import NetWorkIcon from '@/assets/images/common/svg/network.svg?react';
import { formatUser } from '@/store/client';
import { SearchTabs } from './helpers';
import type { SearchResults } from './helpers';

export interface ResultContactsProps {
  searchResults: SearchResults;
  searchValue: string;
  setActive: Dispatch<SetStateAction<SearchTabs>>;
}
interface UserListHeaderProps {
  title: string;
  list: any[] | undefined;
  setActive: () => void;
}

export const GroupeContacts = ({
  title,
  users,
}: {
  title: string | ReactNode;
  users: User[];
}) => {
  if (users.length === 0) return null;
  return (
    <Box>
      <Box fontSize="xs" color="fg.muted" fontWeight="medium" p="2.5">
        {title}
      </Box>
      <Box className="List" bg="bg" p="1" borderRadius="lg">
        {users?.map((item, index) => (
          <ResultUser key={index} item={item} hiddenBorder={index === users.length - 1} />
        ))}
      </Box>
    </Box>
  );
};
export const ResultUser = ({ item, hiddenBorder }: { item: User; hiddenBorder: boolean }) => {
  const isKeyErr = !(item.have_group_key && item.have_user_key);
  item = formatUser(item);
  const navigate = useNavigate();
  const { setGlobalSearchIsOpen, fetchProfileUser } = useActionsStore(
    useShallowEnhance(['setGlobalSearchIsOpen', 'fetchProfileUser'])
  );
  const { createSingleSession } = useAppStore(useShallowEnhance(['createSingleSession']));
  const handleAvatarClick = useCallback(
    async (user: User) => {
      fetchProfileUser(user.user_id);
      // close avatar
      setGlobalSearchIsOpen(false);
    },
    [fetchProfileUser, setGlobalSearchIsOpen]
  );

  const handleUserClick = useCallback(
    async (user: User) => {
      try {
        const session = await createSingleSession(user.user_id);
        navigate(Paths.MESSAGES_CHATS + '/' + session?.chat_id);
        setGlobalSearchIsOpen(false);
      } catch (e) {
        console.log(e);
      }
    },
    [createSingleSession, navigate, setGlobalSearchIsOpen]
  );
  return (
    <UserItem
      key={item.user_id}
      {...item}
      onlyFinalName={false}
      error={isKeyErr}
      avatar={item.avatar}
      cursor={isKeyErr ? 'not-allowed' : 'pointer'}
      borderColor={hiddenBorder ? 'transparent' : 'border.subtle'}
      onAvatarClick={() => {
        handleAvatarClick(item);
      }}
      onClick={() => {
        if (isKeyErr) return;
        handleUserClick(item);
      }}
    />
  );
};

export const UserListHeader: FC<UserListHeaderProps> = (props) => {
  const { title, list, setActive } = props;
  return (
    <HStack justify="space-between">
      <Span>{title}</Span>
      {gt(size(list), 3) && (
        <Text
          cursor="pointer"
          onClick={() => {
            setActive();
          }}
        >
          <MyTrans i18nKey="filter.more" />
        </Text>
      )}
    </HStack>
  );
};
export const ResultTopContacts = (props: Omit<ResultContactsProps, 'searchValue'>) => {
  const { t } = useTranslation();
  const { searchResults, setActive } = props;
  const { contactsUsers, notContactsUsers } = searchResults;
  return (
    <>
      <GroupeContacts
        title={
          <UserListHeader
            title={t('filter.membersInContacts')}
            list={contactsUsers}
            setActive={() => setActive(SearchTabs.Contacts)}
          />
        }
        users={(contactsUsers ?? []).slice(0, 3)}
      />
      <GroupeContacts
        title={
          <UserListHeader
            title={t('filter.membersNotInContacts')}
            list={notContactsUsers}
            setActive={() => setActive(SearchTabs.Contacts)}
          />
        }
        users={(notContactsUsers ?? []).slice(0, 3)}
      />
    </>
  );
};
export const ExtraElement = (
  props: Omit<ResultContactsProps, 'searchResults'> & {
    mt?: string | number;
  }
) => {
  const { searchValue, setActive, ...rest } = props;
  const { t } = useTranslation();
  return (
    <>
      {searchValue && (
        <HStack
          w="full"
          h="11.5"
          borderRadius="lg"
          p="2.5"
          boxSizing="border-box"
          justify="space-between"
          fontSize="sm"
          bg="bg"
          {...rest}
        >
          <HStack gap="2">
            <NetWorkIcon />
            <Text fontWeight="medium" color="fg.default">
              {t('filter.searchAtGlobal')}: <Span color="brand.100">{searchValue}</Span>
            </Text>
          </HStack>
          <Text
            color="brand.100"
            cursor="pointer"
            onClick={() => {
              setActive(SearchTabs.Global);
            }}
          >
            {t('buttons.view')}
          </Text>
        </HStack>
      )}
    </>
  );
};
const ResultContacts = ({ searchResults, searchValue, setActive }: ResultContactsProps) => {
  const { t } = useTranslation();
  const { contactsUsers, notContactsUsers } = searchResults;

  if (!contactsUsers?.length && !notContactsUsers?.length)
    return (
      <Stack gap="4" className="fadeIn">
        <Empty />
        <ExtraElement searchValue={searchValue} setActive={setActive} mt="10" />
      </Stack>
    );

  return (
    <Stack gap="4" className="fadeIn">
      <GroupeContacts title={t('filter.membersInContacts')} users={contactsUsers ?? []} />
      <GroupeContacts title={t('filter.membersNotInContacts')} users={notContactsUsers ?? []} />
      <ExtraElement searchValue={searchValue} setActive={setActive} />
    </Stack>
  );
};

const MemoResultContacts = memo(ResultContacts);

export { MemoResultContacts as ResultContacts };
